import React, { useMemo } from 'react';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MuiAppBar from '@mui/material/AppBar';
import { styled } from '@mui/material/styles';
import { DrawerWidth } from '../../utils/constants.js';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useIsOnMobile } from '../../hooks/layoutHooks.js';
import { Slide, useScrollTrigger, Box, Avatar } from '@mui/material';
import { useSelector } from 'react-redux';
import { getUserFullName } from '../../utils/user.utils.js';

// Header colors - black, white, gray, red
const headerColors = {
  background: 'linear-gradient(90deg, #1a1a1a 0%, #121212 100%)',
  textPrimary: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.85)',
  accentColor: '#ef4444',
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  background: headerColors.background,
  boxShadow: '0 2px 20px rgba(0, 0, 0, 0.3)',
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('md')]: {
    ...(open && {
      marginLeft: DrawerWidth,
      width: `calc(100% - ${DrawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  },
}));

const HideOnScroll = ({ children, isMobile }) => {
  const trigger = useScrollTrigger();

  if (!isMobile) return <>{children}</>

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}


export const AppHeader = ({ open, handleDrawerToggle }) => {
  const lngsltd = JSON.parse(localStorage.getItem('languageSelected'));
  const userInfos = useSelector((state) => state.authState.userInfos)
  const navigate = useNavigate();
  const isMobile = useIsOnMobile()
  const userFullName = useMemo(() => getUserFullName(userInfos), [userInfos])

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    return parts.length > 1 
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  const handleLogout = () => {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('cultureSelected');
    localStorage.removeItem('languageSelected');
    navigate('/');
  };

  return (
    <HideOnScroll isMobile={isMobile}>
      <AppBar open={open}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={[
              {
                marginRight: { xs: 2, md: 4 },
                color: 'rgba(255,255,255,0.9)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              },
              open && { display: { md: "none" } },
            ]}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              fontWeight: 700,
              letterSpacing: '3px',
              color: '#ffffff',
            }}
          >
            SKEIDAR
          </Typography>

          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar 
                sx={{ 
                  width: { xs: 32, md: 36 }, 
                  height: { xs: 32, md: 36 },
                  bgcolor: headerColors.accentColor,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                  fontWeight: 600,
                }}
              >
                {getInitials(userFullName)}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.6)', 
                    fontSize: '0.7rem',
                    lineHeight: 1.2
                  }}
                >
                  {lngsltd?.["Welcome"]}
                </Typography>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    lineHeight: 1.2
                  }}
                >
                  {userFullName || 'User'}
                </Typography>
              </Box>
            </Box>
            
            <Tooltip 
              title={lngsltd?.["Logout"]} 
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1a1a1a',
                    color: '#fff',
                    fontSize: '0.75rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  },
                },
                arrow: {
                  sx: {
                    color: '#1a1a1a',
                  },
                },
              }}
            >
              <IconButton 
                size={isMobile ? "small" : "medium"} 
                aria-label="logout" 
                onClick={handleLogout} 
                sx={{ 
                  color: "rgba(255,255,255,0.8)",
                  ml: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.15)',
                    color: '#ef4444',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <LogoutRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
    </HideOnScroll>
  );
}

